import React, { useState, FormEvent } from "react";
import { Modal } from "../ui/modal";
import Button from "../ui/button/Button";
import Input from "../form/input/InputField";
import { Tag, DollarSign, Book, Users, FileText, MapPin, CheckCircle, Building2 } from 'lucide-react';
import type { Excursion } from "../../hooks/useApi";
import { toast } from "react-hot-toast";

interface CreateExcursionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddExcursion: (excursion: Omit<Excursion, "id" | "booked">) => Promise<Excursion>;
  onSuccess?: (newExcursion: Excursion) => void;
}

interface FormErrors {
  name?: string;
  description?: string;
  priceB2CAdult?: string;
  priceB2CChild?: string;
  priceB2BAdult?: string;
  priceB2BChild?: string;
  maxCapacity?: string;
  destination?: string;
}

const CreateExcursionModal: React.FC<CreateExcursionModalProps> = ({
  isOpen,
  onClose,
  onAddExcursion,
  onSuccess
}) => {
  const [excursionFormData, setExcursionFormData] = useState({
    name: "",
    description: "",
    priceB2CAdult: "",
    priceB2CChild: "",
    priceB2BAdult: "",
    priceB2BChild: "",
    maxCapacity: "",
    active: true,
    destination: "",
    isThirdParty: false
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!excursionFormData.name.trim()) {
      newErrors.name = 'El nombre de la excursión es requerido';
    }
    
    if (!excursionFormData.description.trim()) {
      newErrors.description = 'La descripción es requerida';
    }
    
    if (!excursionFormData.priceB2CAdult || parseFloat(excursionFormData.priceB2CAdult) <= 0) {
      newErrors.priceB2CAdult = 'Ingrese un precio válido mayor a 0';
    }
    
    if (!excursionFormData.priceB2BAdult || parseFloat(excursionFormData.priceB2BAdult) <= 0) {
      newErrors.priceB2BAdult = 'Ingrese un precio válido mayor a 0';
    }
    
    if (!excursionFormData.maxCapacity || parseInt(excursionFormData.maxCapacity) <= 0) {
      newErrors.maxCapacity = 'La capacidad máxima debe ser mayor a 0';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setExcursionFormData({
      ...excursionFormData,
      [name]: value
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const newExcursion = await onAddExcursion({
        ...excursionFormData,
        priceB2CAdult: Number(excursionFormData.priceB2CAdult),
        priceB2CChild: excursionFormData.priceB2CChild === "" ? null : Number(excursionFormData.priceB2CChild),
        priceB2BAdult: Number(excursionFormData.priceB2BAdult),
        priceB2BChild: excursionFormData.priceB2BChild === "" ? null : Number(excursionFormData.priceB2BChild),
        maxCapacity: Number(excursionFormData.maxCapacity),
        destination: excursionFormData.destination || '',
        isThirdParty: excursionFormData.isThirdParty
      });
      
      setExcursionFormData({
        name: "",
        description: "",
        priceB2CAdult: "",
        priceB2CChild: "",
        priceB2BAdult: "",
        priceB2BChild: "",
        maxCapacity: "",
        active: true,
        destination: "",
        isThirdParty: false
      });
      
      if (newExcursion) {
        onSuccess?.(newExcursion);
        toast.success('Excursión creada exitosamente');
      }
      onClose();
    } catch (error) {
      console.error("Error al crear excursión:", error);
      toast.error("Error al crear la excursión");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Agregar nueva excursión">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información Básica */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
            <Tag className="w-4 h-4 mr-1.5 text-primary-500" />
            Información Básica
          </h3>
          <div className="grid grid-cols-1 gap-4">
            {/* Nombre */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Nombre
                <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                name="name"
                value={excursionFormData.name}
                onChange={handleInputChange}
                placeholder="Nombre de la excursión"
                error={errors.name ? true : false}
                hint={errors.name}
                className="w-full"
              />
            </div>
            
            {/* Descripción */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Descripción
                <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={excursionFormData.description}
                onChange={handleInputChange}
                placeholder="Descripción detallada de la excursión"
                rows={3}
                className={`w-full px-3 py-2 border rounded-md ${
                  errors.description 
                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                } dark:bg-gray-800 dark:border-gray-700 dark:text-white`}
              />
              {errors.description && (
                <p className="text-xs text-red-500">{errors.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Precios y Capacidad */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
            <DollarSign className="w-4 h-4 mr-1.5 text-primary-500" />
            Precios y Capacidad
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Precios B2C */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Precios B2C (Cliente Final)
              </label>
              <div className="space-y-2">
                <Input
                  type="number"
                  name="priceB2CAdult"
                  value={excursionFormData.priceB2CAdult}
                  onChange={handleInputChange}
                  placeholder="Precio Adulto"
                  step="0.01"
                  min="0"
                  error={errors.priceB2CAdult ? true : false}
                  hint={errors.priceB2CAdult}
                  className="w-full"
                />
                <Input
                  type="number"
                  name="priceB2CChild"
                  value={excursionFormData.priceB2CChild}
                  onChange={handleInputChange}
                  placeholder="Precio Niño"
                  step="0.01"
                  min="0"
                  error={errors.priceB2CChild ? true : false}
                  hint={errors.priceB2CChild}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Dejar en blanco si la excursión no admite niños
                </p>
              </div>
            </div>

            {/* Precios B2B */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Precios B2B (Agencias)
              </label>
              <div className="space-y-2">
                <Input
                  type="number"
                  name="priceB2BAdult"
                  value={excursionFormData.priceB2BAdult}
                  onChange={handleInputChange}
                  placeholder="Precio Adulto"
                  step="0.01"
                  min="0"
                  error={errors.priceB2BAdult ? true : false}
                  hint={errors.priceB2BAdult}
                  className="w-full"
                />
                <Input
                  type="number"
                  name="priceB2BChild"
                  value={excursionFormData.priceB2BChild}
                  onChange={handleInputChange}
                  placeholder="Precio Niño"
                  step="0.01"
                  min="0"
                  error={errors.priceB2BChild ? true : false}
                  hint={errors.priceB2BChild}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Dejar en blanco si la excursión no admite niños
                </p>
              </div>
            </div>
          </div>

          {/* Capacidad y Destino */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Capacidad máxima
                <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                name="maxCapacity"
                value={excursionFormData.maxCapacity}
                onChange={handleInputChange}
                placeholder="Número de personas"
                min="1"
                error={errors.maxCapacity ? true : false}
                hint={errors.maxCapacity}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Destino
              </label>
              <Input
                type="text"
                name="destination"
                value={excursionFormData.destination}
                onChange={handleInputChange}
                placeholder="Ej: Saona, Catalina, etc."
                error={errors.destination ? true : false}
                hint={errors.destination}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Configuración */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
            <CheckCircle className="w-4 h-4 mr-1.5 text-primary-500" />
            Configuración
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Estado */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                Estado
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={excursionFormData.active}
                  onChange={(e) => setExcursionFormData({ ...excursionFormData, active: e.target.checked })}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="active" className="text-sm text-gray-700 dark:text-gray-300">
                  Activa
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Las excursiones inactivas no aparecerán en los listados públicos
              </p>
            </div>

            {/* Tipo de Excursión */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                Tipo de Excursión
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isThirdParty"
                  name="isThirdParty"
                  checked={excursionFormData.isThirdParty}
                  onChange={(e) => setExcursionFormData({ ...excursionFormData, isThirdParty: e.target.checked })}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600"
                />
                <label htmlFor="isThirdParty" className="text-sm text-gray-700 dark:text-gray-300">
                  Excursión de terceros
                </label>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Marque esta opción si la excursión es operada por un tercero
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Creando...' : 'Crear excursión'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateExcursionModal; 